/* eslint-disable no-underscore-dangle */
/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { cn } from "@/lib/utils";
import withAuthenticator from "@/hoc/withAuthenticator/withAuthenticator";

import FiltersBar, { CLEAN_FILTERS } from "@/components/conversation-insights/filters-bar";
import Insights from "@/components/conversation-insights/insights";
import Leaderboard from "@/components/conversation-insights/leaderboard";
import { MultiSelectOption } from "@/components/ui/multi-select-button";
import {
  ConversationFilters,
  useConversationsInsights,
} from "@/hooks/useConversationsInsights";
import type { RootState } from "@/store/reducers";
import { API_BASE_URL, ROUTES } from "@/utils/constants";
import { getCRMStatus } from "@/handlers/handlers";
import ConversationsService from "@/services/conversations/conversations.service";
import OrganisationService from "@/services/organisation/organisation.service";
import CustomerService from "@/services/customer/customer.service";
import { debounce } from "@material-ui/core";
import useModal from "@/hooks/useModal";
import DownloadReportModal from "@/components/conversation-insights/download-report-modal";
import HttpClient from "@/utils/http-client";
import SideClose from "../../public/icons/side-close.svg";


const pitchGapOptions: MultiSelectOption[] = [
  { value: "case_study", label: "Case Study" },
  { value: "feature_x", label: "Feature X" },
];

const intentOptions: MultiSelectOption[] = [
  { value: "HIGH_INTENT", label: "High" },
  { value: "MODERATE", label: "Moderate" },
  { value: "LOW_INTENT", label: "Low" },
  { value: "NEUTRAL", label: "Neutral" },
];


type RawAgent = {
  assigned_user_id: number;
  assigned_user_first_name: string;
  assigned_user_last_name: string | null;
  assigned_user_avatar_url: string | null;
  assigned_user_email: string;
};

function buildAgentOptions(list: RawAgent[]): MultiSelectOption[] {
  return list.map((a) => {
    const fullName = `${a.assigned_user_first_name} ${a.assigned_user_last_name ?? ""}`.trim();
    return {
      value: String(a.assigned_user_id),               // ⬅ used as the stable ID
      label: fullName || a.assigned_user_email,        // fallback to email if no name
      avatarUrl: a.assigned_user_avatar_url ?? undefined,
    };
  });
}

function buildQualificationQuestions(list: any[]) {
  return list.map((q) => ({
    capture_id: q?._id,
    qualification_question: q.qualification_question,
    /* API uses "bool" – that's already allowed by the union type.
       If the backend ever sends "boolean", keep it as-is.            */
    return_type: q.return_type as "string" | "integer" | "bool" | "boolean",
    capture_output: [],                // start empty; user will fill this
  }));
}

function buildStatusOptions(list: any[]): MultiSelectOption[] {
  return (
    list
      // keep only “active” ones and de-duplicate by id
      .filter((s) => s.status === "active")
      .filter(
        (s, i, arr) => arr.findIndex((t) => t.id === s.id) === i,
      )
      // sort alphabetically
      .sort((a, b) => a.name.localeCompare(b.name))
      // map to MultiSelectOption
      .map((s) => ({
        value: String(s.id),
        label: s.name.replace(/_/g, " "), // nice display for “IN_PROGRESS”
      }))
  );
}

export function buildCustomerOptions(list: any[]): MultiSelectOption[] {
  return list.map((c) => {
    const customerName = c?.contact_name ?? c?.customer_name;
    const customerEmail = c?.contact_email;
    const customerPhone = c?.contact_phone;

    return {
      value: String(c.customer_id),
      label: [customerName, customerEmail, customerPhone].filter(Boolean)?.join(' | '),       
      avatarUrl: c.avatar_url ?? undefined 
    };
  });
}

export function buildObjectionOptions(list: string[]): MultiSelectOption[] {
  return list
    .filter(Boolean)
    .map((text) => ({
      value: text,   
      label: text,
      avatarUrl: undefined,
    }));
}

export function buildPitchGapOptions(list: any[]) {
  return list.map((listItem) => ({
    value: listItem?.id,                              
    label: listItem?.label,
    avatarUrl: undefined,                           
  }));
}

function pruneEmptyArrays(filters = {}) {
  return Object.fromEntries(
    Object.entries(filters).filter(([, v]) => !(Array.isArray(v) && v.length === 0))
  );
}

/* ────────────────────────────────────────────────────────────
 *  Component
 * ──────────────────────────────────────────────────────────── */
function ConversationInsights() {
  const { user, isAdmin, isManager } = useSelector((s: RootState) => s.common);
  const { isOpen, openModal, closeModal } = useModal();

  const selectedWorkspace = useSelector(
      (state: RootState) => state.common.selectedWorkspace[0],
      
    );

    const [agents, setAgents] = useState([])
    const [qualificationQuestions, setQualificationQuestions] = useState([])
    const [status, setStatus] = useState([])
    const [customerOptions, setCustomerOptions] = React.useState([]);
    const [customersLoading, setCustomersLoading] = React.useState(false);

  const [page, setPage] = useState(1);
  const limit = 10;
  // const PAGE_SIZE = 6;   
  // const sortField = "last_conversation";
  // const sortOrder: "asc" | "desc" = "desc";

  const debounceSuggestionsWorkspace = debounce((newValue: string) => {
        (async () => {
          try {
            // console.log("debounceSuggestionsWorkspace",{newValue})
             setCustomersLoading(true);
          const workspace_id = String(selectedWorkspace?.id);
          const method = CustomerService.getSearchSuggestionsWorkspace;
          // console.log({newValue, workspace_id, user})
          const { data } = await method(newValue, 10, workspace_id, !isAdmin && isManager ? user.id : null);

          // console.log({data})
  
          const customerList = data?.data?.customerList?.map(i => ({
            customer_id: i?.customer_id,
            customer_name: i?.customer_name,
            contact_name: i?.contact_name,
            contact_email: i?.contact_email,
            contact_phone: i?.contact_phone,
          }));
          // console.log({customerList})

          setCustomerOptions(buildCustomerOptions(customerList))
          setCustomersLoading(false);
          } catch (error) {
            setCustomersLoading(false);
            console.log({error})
          }
        
        })();
      }, 400)

  const [filters, setFilters] = useState(CLEAN_FILTERS);
  const [previousAppliedFilters, setPreviousAppliedFilters] = useState(filters)

  const handleDownloadReport = async () => {
    try {
      const payload = pruneEmptyArrays(filters)
      const response = await HttpClient.post(
        `${API_BASE_URL}/v1/customer/insights/workspace/${selectedWorkspace?.id}/reports`,{filters: payload}
      );

      openModal();
      console.log(response)
    } catch (error) {
      console.log({error})
    }
  }

  const fetchCrmStatus = async () => {
    try {
      const response = await getCRMStatus();
      const allStatus = response;
      setStatus(buildStatusOptions(allStatus))
    } catch (error) {
      console.log({error})
    }
 
  }

  const fetchAllAssignes = async () => {
    try {
      const response = await ConversationsService.getAllAssigneeUsers(
        selectedWorkspace?.id,
      );
      const assigneeUsers = response?.data?.data;
  
      setAgents(buildAgentOptions(assigneeUsers))
    } catch (error) {
      console.log({error})
    }


  }

  const fetchQUalificationQuestions = async () => {
    try {
      
      const workspace_id = selectedWorkspace?.id;
      const response =
        await OrganisationService.getWorkspaceQualificationQuestions(
          workspace_id,
        );
      const questions = response?.data?.data
        .filter(item => !item?.deleted_at && item.is_active)
        .map(item => ({ ...item, filter: false }));

        setQualificationQuestions(buildQualificationQuestions(questions))
    } catch (error) {
      console.log({error})
    }
 
  }

  /* hook: fetch data */
  const {
    objections,
    overview,
    intentSpread,
    keyInsights,
    customers,
    agentPitchGaps,
    loadingObjections,
    loadingOverview,
    loadingIntentSpread,
    loadingKeyInsights,
    loadingCustomers,
    loadingAgentPitchGaps,
    fetchObjections,
    fetchOverview,
    fetchIntentSpread,
    fetchKeyInsights,
    fetchCustomers,
    fetchAgentPitchGaps,
  } = useConversationsInsights(
    selectedWorkspace?.id,
    page,
    limit,
    pruneEmptyArrays(filters),
  );

  useEffect(() => {
    fetchObjections();
    fetchAgentPitchGaps();
    fetchOverview();
    fetchIntentSpread();
    fetchKeyInsights();
    fetchCustomers();

    // get filters data
    fetchCrmStatus();
    fetchAllAssignes();
    fetchQUalificationQuestions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (cleanPayload?: any) => {
    setPreviousAppliedFilters(cleanPayload ?? filters);
    const payload = cleanPayload ? pruneEmptyArrays(cleanPayload) : pruneEmptyArrays(filters)
    fetchOverview(payload);
    fetchIntentSpread(payload);
    fetchKeyInsights(payload);
    fetchCustomers(payload);
  }

  const handlePageChange = (pageNumber) => {
    if(pageNumber){
      const payload = pruneEmptyArrays(filters)
      fetchCustomers(payload, pageNumber);
      setPage(pageNumber);
    }
  }

  const handleIntentClick = (intent: string) => {
    const updatedFilters = {...filters, intents: [intent]}
    setFilters(updatedFilters)
    setPreviousAppliedFilters(updatedFilters);
    const payload = pruneEmptyArrays(updatedFilters)
    fetchOverview(payload);
    fetchIntentSpread(payload);
    fetchKeyInsights(payload);
    fetchCustomers(payload);
  }

  /* sidebar toggle */
  const [openLeaderboard, setOpenLeaderboard] = useState(false);

  return (
    <div className={cn("flex h-full ")}>
      {/* ───── Left main area ───── */}
      <div className={cn(
        "border-r border-[#E4E4E7] w-full overflow-auto",
        // width of the scrollbar in WebKit
        "[&::-webkit-scrollbar]:w-2",
        // track color in WebKit
        "[&::-webkit-scrollbar-track]:bg-[#F4F4F5]",
        // thumb color + rounded in WebKit
        "[&::-webkit-scrollbar-thumb]:bg-[#09090B]",
        "[&::-webkit-scrollbar-thumb]:rounded-full",
        // Firefox: thin + color (thumb then track)
        "[scrollbar-width:thin]",
        "[scrollbar-color:#09090B_#F4F4F5]",
        {
          "w-[calc(100%-366px)]": openLeaderboard,
          "w-[calc(100%-88px)]": !openLeaderboard,
        })}>
        {/* Header + filters */}
        <div className="border-b border-[#E4E4E7] space-y-2 p-4">
          <h1 className="text-xl font-bold">Conversations Insights</h1>
          <p className="text-sm text-muted-foreground !mt-0">
            View all conversations insights related to your team.
          </p>

          <FiltersBar
            customersLoading={customersLoading}
            debounceSuggestionsWorkspace={debounceSuggestionsWorkspace}
            customerOptions={customerOptions}
            agentOptions={agents}
            statusOptions={status}
            concernOptions={buildObjectionOptions(objections)}
            pitchGapOptions={buildPitchGapOptions(agentPitchGaps)}
            intentOptions={intentOptions}
            qualificationQuestions={qualificationQuestions}
            onChange={setFilters}
            globalFilters={filters}
            onSubmit={handleSubmit}
            previousAppliedFilters={previousAppliedFilters}
            loadingOverview={loadingOverview}
            loadingIntentSpread={loadingIntentSpread}
            loadingKeyInsights={loadingKeyInsights}
            // setPreviousAppliedFilters={setPreviousAppliedFilters}
          />
        </div>

        {/* Metrics + tables */}
        <Insights
          overview={overview}
          intentSpread={intentSpread}
          customers={customers}
          loadingOverview={loadingOverview}
          loadingIntentSpread={loadingIntentSpread}
          loadingCustomers={loadingCustomers}
          loadingKeyInsights={loadingKeyInsights}
          loadingObjections={loadingObjections}
          keyInsights={keyInsights}
          objections={objections}
          handleDownloadReport={handleDownloadReport}
          handlePageChange={handlePageChange}
          page={page}
          handleIntentClick={handleIntentClick}
        />
      </div>

      {/* ───── Right leaderboard pane ───── */}
      <div
        className={cn("relative transition-all duration-300", {
          "w-[366px]": openLeaderboard,
          "w-[88px]": !openLeaderboard,
        })}
      >
        <button
          type="button"
          className="absolute -left-5 top-4"
          onClick={() => setOpenLeaderboard((p) => !p)}
        >
          <SideClose className={`
            transform
            transition-transform
            duration-200
            ${!openLeaderboard ? 'rotate-180' : 'rotate-0'}
          `} />
        </button>

        <h2 className="px-4 font-bold mt-5 break-words">Leaderboard</h2>

        {openLeaderboard && (
          <>
            <p className="px-4 text-sm text-muted-foreground">
              Sort your team by the key metrics
            </p>
            <Leaderboard />
          </>
        )}
      </div>
      {isOpen && (
        <DownloadReportModal
          open={isOpen}
          handleModalClose={closeModal}
        />
      )}
    </div>
  );
}

/* wrap with your HOC */
export default withAuthenticator(ConversationInsights, {
  redirectPath: `/auth/${ROUTES.AUTH_TYPES.LOGIN}`,
  onlyAdmin: true,
});